import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  HeartPulse,
  Home,
  Sparkles,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import type { DashboardBriefItem } from "@/api/dashboard";
import { Card } from "@/components/ui/card";

type Props = {
  items: DashboardBriefItem[];
};

export function MinistryBriefCard({ items }: Props) {
  return (
    <Card className="h-full min-h-[520px] overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-violet-500/15 p-3 text-violet-400">
            <Sparkles size={21} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Today&apos;s Ministry Brief
            </h2>
            <p className="text-sm text-slate-500">
              What changed and what matters today
            </p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-slate-800">
        {items.map((item) => {
          const visual = getVisual(item);

          const content = (
            <div className="flex items-center gap-4 px-6 py-5">
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${visual.className}`}
              >
                {visual.icon}
              </div>

              <p className="flex-1 text-sm leading-6 text-slate-300">
                {item.message}
              </p>

              {item.to && (
                <ArrowRight size={17} className="text-slate-600" />
              )}
            </div>
          );

          if (!item.to) {
            return <div key={item.id}>{content}</div>;
          }

          return (
            <Link
              key={item.id}
              to={item.to}
              className="block transition hover:bg-slate-800/40"
            >
              {content}
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

function getVisual(item: DashboardBriefItem) {
  const normalizedType = item.type.toUpperCase();

  if (item.severity === "CRITICAL") {
    return {
      icon: <AlertTriangle size={18} />,
      className: "bg-rose-500/15 text-rose-400",
    };
  }

  if (normalizedType === "HOUSEHOLD") {
    return {
      icon: <Home size={18} />,
      className: "bg-amber-500/15 text-amber-400",
    };
  }

  if (normalizedType === "CARE") {
    return {
      icon: <HeartPulse size={18} />,
      className: "bg-rose-500/15 text-rose-400",
    };
  }

  if (
    normalizedType === "MEMBER" ||
    normalizedType === "VISITOR"
  ) {
    return {
      icon: <Users size={18} />,
      className: "bg-indigo-500/15 text-indigo-400",
    };
  }

  return {
    icon: <CheckCircle2 size={18} />,
    className: "bg-emerald-500/15 text-emerald-400",
  };
}